/**
 * Formats a price number with thousand separators for better readability
 * @param {number} price - The price to format
 * @param {string} locale - The locale to use for formatting (optional, defaults to stored locale)
 * @returns {string} - Formatted price string
 */
export const formatPrice = (price, locale = null) => {
  if (!price || isNaN(price)) return price;

  // Get locale from localStorage or use provided locale, fallback to 'en-US'
  const currentLocale = locale || localStorage.getItem("ln") || "en";

  // Map i18n language codes to Intl locale codes
  const localeMap = {
    en: "en-US",
    tr: "tr-TR",
    fr: "fr-FR",
    de: "de-DE",
    es: "es-ES",
    pt: "pt-PT",
    nl: "nl-NL",
    el: "el-GR",
  };

  const intlLocale = localeMap[currentLocale] || "en-US";

  return new Intl.NumberFormat(intlLocale).format(price);
};

/**
 * Formats a price with currency for display
 * @param {number} price - The price to format
 * @param {string} currency - The currency code (e.g., 'EUR', 'TRY')
 * @param {string} locale - The locale to use for formatting (optional, defaults to stored locale)
 * @returns {string} - Formatted price with currency
 */
export const formatPriceWithCurrency = (price, currency, locale = null) => {
  const formattedPrice = formatPrice(price, locale);
  return `${formattedPrice} ${currency}`;
};

/**
 * Formats a price with full currency formatting using Intl.NumberFormat
 * @param {number} price - The price to format
 * @param {string} currency - The currency code (e.g., 'EUR', 'TRY')
 * @param {string} locale - The locale to use for formatting (optional, defaults to stored locale)
 * @returns {string} - Formatted price with localized currency formatting
 */
export const formatPriceWithLocalizedCurrency = (
  price,
  currency,
  locale = null
) => {
  if (!price || isNaN(price)) return `${price} ${currency}`;

  // Get locale from localStorage or use provided locale, fallback to 'en-US'
  const currentLocale = locale || localStorage.getItem("ln") || "en";

  // Map i18n language codes to Intl locale codes
  const localeMap = {
    en: "en-US",
    tr: "tr-TR",
    fr: "fr-FR",
    de: "de-DE",
    es: "es-ES",
    pt: "pt-PT",
    nl: "nl-NL",
    el: "el-GR",
  };

  const intlLocale = localeMap[currentLocale] || "en-US";

  try {
    return new Intl.NumberFormat(intlLocale, {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  } catch (error) {
    // Fallback to simple formatting if currency is not supported
    return formatPriceWithCurrency(price, currency, locale);
  }
};
